import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import type { SidebarLinkItem } from '@/shared/ui/Sidebar'
import { isMasterByEmail, normalizeRole, type AppRole } from '@/modules/shared'
import { Settings as SettingsIcon } from 'lucide-react'
import { NAV_DEFS } from '../menus/defs'
import { ICONS } from '../menus/icons'
import { getNavConfig, type NavConfigItem } from '@/modules/settings/services/navSettingsService'
import { subscribeNavChanged } from '@/modules/navigation/events'
import { useAnalysisEnabled } from '@/modules/analises'
import { useVisionEnabled } from '@/modules/visoes'
import { useChatEnabled } from '@/modules/chat'
import { useRewardsEnabled } from '@/modules/rewards'
import { useRevshareAllowed } from '@/modules/revshare'

export function useNavLinks(): { links: SidebarLinkItem[]; effectiveRole: AppRole; isMasterEmail: boolean } {
  const { user, profile } = useAuth()
  const masterEmail = isMasterByEmail(user?.email)
  const effectiveRole = normalizeRole(
    profile?.role || (user as any)?.user_metadata?.role || (user as any)?.app_metadata?.role,
  )
  const analysisEnabled = useAnalysisEnabled()
  const visionEnabled = useVisionEnabled()
  const chatEnabled = useChatEnabled()
  const rewardsEnabled = useRewardsEnabled()
  const revshareAllowed = useRevshareAllowed()

  const roleForNav = (masterEmail ? 'master' : (effectiveRole as any)) as 'master' | 'member' | 'afiliado' | null

  const defs = React.useMemo(() => {
    if (!roleForNav) return [] as typeof NAV_DEFS
    const byRole = NAV_DEFS.filter((d) => d.roles.includes(roleForNav))
    let out = byRole
    // Analises Cripto: so expor o menu quando o acesso do usuario estiver confirmado como habilitado
    // (evita exibir o item durante o carregamento e depois esconder).
    if (analysisEnabled !== true) {
      out = out.filter((d) => d.key !== 'analysis_cripto')
    }
    if (visionEnabled !== true) {
      out = out.filter((d) => d.key !== 'visoes')
    }
    if (chatEnabled !== true) {
      out = out.filter((d) => d.key !== 'chat')
    }
    if (rewardsEnabled === false) {
      out = out.filter((d) => d.key !== 'rewards')
    }
    if (revshareAllowed === false) {
      out = out.filter((d) => d.key !== 'repasses')
    }
    if (roleForNav === 'master') {
      out = out.map((d) => (d.key === 'rewards' ? { ...d, to: '/rewards-master-dashboard' } : d))
    }
    return out
  }, [roleForNav, analysisEnabled, visionEnabled, chatEnabled, rewardsEnabled, revshareAllowed])

  const [links, setLinks] = React.useState<SidebarLinkItem[]>(() => defs.map((d) => toSidebarItem(d)))

  React.useEffect(() => {
    // Quando defs mudar (role ou gate de análises), re-aplica ordem/configuração atual básica
    setLinks(defs.map((d) => toSidebarItem(d)))
  }, [defs])

  React.useEffect(() => {
    let cancelled = false
    if (!roleForNav) {
      setLinks(defs.map((d) => toSidebarItem(d)))
      return
    }
    ;(async () => {
      try {
        const cfg = await getNavConfig(roleForNav)
        if (cancelled) return
        setLinks(applyNavConfig(defs, cfg))
      } catch {
        setLinks(defs.map((d) => toSidebarItem(d)))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [roleForNav, defs])

  // Recarrega quando a configuração é salva sem recarregar a página
  React.useEffect(() => {
    const unsub = subscribeNavChanged(() => {
      if (!roleForNav) {
        setLinks(defs.map((d) => toSidebarItem(d)))
        return
      }
      ;(async () => {
        try {
          const cfg = await getNavConfig(roleForNav)
          setLinks(applyNavConfig(defs, cfg))
        } catch {
          setLinks(defs.map((d) => toSidebarItem(d)))
        }
      })()
    })
    return () => unsub()
  }, [roleForNav, defs])

  return { links, effectiveRole, isMasterEmail: masterEmail }

  function applyNavConfig(defItems: typeof NAV_DEFS, cfg: NavConfigItem[] | null | undefined): SidebarLinkItem[] {
    if (!cfg || !cfg.length) {
      return defItems.map((d) => toSidebarItem(d))
    }
    const byKey = new Map(defItems.map((d) => [d.key, d]))
    const cfgByKey = new Map(cfg.map((it) => [it.key, it]))
    const orderedDefs = cfg
      .map((it) => byKey.get(it.key as any))
      .filter(Boolean) as typeof NAV_DEFS
    const restDefs = defItems.filter((d) => !cfgByKey.has(d.key))
    const merged = [...orderedDefs, ...restDefs].filter((d) => {
      const item = cfgByKey.get(d.key)
      return item?.visible !== false
    })
    return merged.map((d) => toSidebarItem(d, cfgByKey))
  }

  function toSidebarItem(
    d: (typeof NAV_DEFS)[number],
    cfgByKey?: Map<string, NavConfigItem>,
  ): SidebarLinkItem {
    const cfg = cfgByKey?.get(d.key)
    const iconId = (cfg?.icon as string) || d.icon
    const label = cfg?.label && cfg.label.trim().length > 0 ? cfg.label : d.label
    return { label, to: d.to, icon: iconFor(iconId) }
  }
  function iconFor(id: string) {
    const Cmp = (ICONS as any)[id] || SettingsIcon
    return <Cmp className="h-[26px] w-[26px] flex-shrink-0 text-foreground" />
  }
}
